import React from 'react';
import { Snackbar, Alert, AlertProps, Box, Collapse } from '@mui/material';

interface ToastProps {
  open: boolean;
  onClose: () => void;
  message: string;
  severity?: AlertProps['severity'];
  autoHideDuration?: number;
  position?: 'top-left' | 'top-right' | 'top-center' | 'bottom-left' | 'bottom-right' | 'bottom-center';
}

/**
 * Notification Toast moderne
 */
export const Toast: React.FC<ToastProps> = ({
  open,
  onClose,
  message,
  severity = 'info',
  autoHideDuration = 5000,
  position = 'top-right',
}) => {
  const anchorOrigin = {
    'top-left': { vertical: 'top' as const, horizontal: 'left' as const },
    'top-right': { vertical: 'top' as const, horizontal: 'right' as const },
    'top-center': { vertical: 'top' as const, horizontal: 'center' as const },
    'bottom-left': { vertical: 'bottom' as const, horizontal: 'left' as const },
    'bottom-right': { vertical: 'bottom' as const, horizontal: 'right' as const },
    'bottom-center': { vertical: 'bottom' as const, horizontal: 'center' as const },
  }[position];

  return (
    <Snackbar
      open={open}
      onClose={onClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={anchorOrigin}
      TransitionComponent={Collapse}
      TransitionProps={{ timeout: 300 }}
      sx={{
        '& .MuiSnackbarContent-root': {
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="filled"
        sx={{
          width: '100%',
          borderRadius: 3,
          fontWeight: 500,
          '& .MuiAlert-icon': {
            alignItems: 'center',
          },
        }}
        iconMapping={{
          success: <Box component="span" sx={{ fontSize: '1.25rem' }}>✓</Box>,
          error: <Box component="span" sx={{ fontSize: '1.25rem' }}>✕</Box>,
          warning: <Box component="span" sx={{ fontSize: '1.25rem' }}>!</Box>,
          info: <Box component="span" sx={{ fontSize: '1.25rem' }}>i</Box>,
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

/**
 * Container pour multiples toasts
 */
export interface ToastContainerProps {
  toasts: Array<{
    id: string;
    message: string;
    severity: AlertProps['severity'];
  }>;
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: '400px',
        width: '100%',
      }}
    >
      {toasts.map((toast, index) => (
        <Collapse
          key={toast.id}
          in={true}
          timeout={300}
          sx={{
            animation: `slideIn 0.3s ease-out ${index * 0.1}s`,
          }}
        >
          <Snackbar
            open={true}
            onClose={() => onRemove(toast.id)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionProps={{ timeout: 300 }}
          >
            <Alert
              onClose={() => onRemove(toast.id)}
              severity={toast.severity}
              variant="filled"
              sx={{
                width: '100%',
                borderRadius: 3,
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Collapse>
      ))}
    </Box>
  );
};

export default Toast;
